import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@/utils/sentry';
import { ErrorCard } from './ErrorCard';
import { needsImportReload } from './importRecovery';

interface Props {
	children: ReactNode;
	/**
	 * Экран вместо упавшего содержимого; retry снимает аварию и рисует содержимое заново.
	 * Без него -- экран ошибки всего приложения с перезагрузкой.
	 */
	fallback?: (retry: () => void) => ReactNode;
	/** Смена значения снимает аварию: переход на другой адрес уводит с упавшей страницы */
	resetKey?: string;
}

interface State {
	hasError: boolean;
	reload: boolean;
	resetKey?: string;
}

/**
 * Ошибка всего приложения: пустое состояние макета с перезагрузкой. Функциональный
 * компонент ради useTranslation: он подписан на смену языка, и экран ошибки
 * перерисовывается вместе с ней. Маршрутизатора здесь может не быть, поэтому
 * единственное действие -- перезагрузка.
 */
function ErrorScreen() {
	const { t } = useTranslation('common');

	return (
		<div className="flex h-screen items-center justify-center">
			<ErrorCard description="errors.unexpectedDescription">
				<button type="button" className="ws-btn" onClick={() => window.location.reload()}>
					{t('errors.reload')}
				</button>
			</ErrorCard>
		</div>
	);
}

/**
 * Error Boundary: перехватывает ошибки рендеринга потомков, отправляет их в отчёт
 * об ошибках и показывает fallback (или экран ошибки приложения).
 * React 19 по-прежнему требует class component для error boundaries, а хуки в
 * классовых компонентах недоступны -- поэтому сама карточка вынесена в
 * ErrorScreen.
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, reload: false, resetKey: props.resetKey };
	}

	static getDerivedStateFromError(error: unknown): Partial<State> {
		return { hasError: true, reload: needsImportReload(error) };
	}

	static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
		return props.resetKey === state.resetKey
			? null
			: { hasError: false, reload: false, resetKey: props.resetKey };
	}

	override componentDidCatch(error: Error, info: ErrorInfo): void {
		captureException(error);
		console.error('Uncaught error in component tree:', error, info);
	}

	private readonly retry = () => {
		if (this.state.reload) window.location.reload();
		else this.setState({ hasError: false });
	};

	override render(): ReactNode {
		if (this.state.hasError) {
			return this.props.fallback ? this.props.fallback(this.retry) : <ErrorScreen />;
		}

		return this.props.children;
	}
}
